// @ts-check
import React from 'react';

// Inline SVG mockups for each tutorial topic. Designed at 720x320 so they
// scale crisply inside Help cards. Re-used by HQ (copy-pasted) so docs stay
// consistent across surfaces.
//
// Each scene renders a stylised UI screenshot: side bar / form / table / etc.
// Pure SVG — no external assets, no fonts beyond system fallback.

const FRAME = { width: 720, height: 320 };

/** @param {string} key */
export function tutorialMockup(key) {
  switch (key) {
    case 'getting-started': return <GettingStarted />;
    case 'product-mrp': return <ProductMrp />;
    case 'product-loose': return <ProductLoose />;
    case 'purchase-grn': return <PurchaseGrn />;
    case 'bill-pos': return <BillPos />;
    case 'recon': return <Recon />;
    case 'barcode-print': return <BarcodePrint />;
    default: return null;
  }
}

function Frame({ children, title }) {
  return (
    <svg viewBox={`0 0 ${FRAME.width} ${FRAME.height}`} className="w-full">
      <rect x={0} y={0} width={FRAME.width} height={FRAME.height} fill="#0a0a0a" />
      <rect x={0} y={0} width={FRAME.width} height={28} fill="#171717" />
      <circle cx={14} cy={14} r={5} fill="#ef4444" /><circle cx={32} cy={14} r={5} fill="#f59e0b" /><circle cx={50} cy={14} r={5} fill="#10b981" />
      <text x={FRAME.width / 2} y={19} fill="#a3a3a3" textAnchor="middle" fontSize={12} fontFamily="sans-serif">{title}</text>
      {children}
    </svg>
  );
}

function GettingStarted() {
  return (
    <Frame title="Katsura POS — first run">
      {/* Sidebar */}
      <rect x={0} y={28} width={160} height={292} fill="#171717" />
      <text x={16} y={56} fill="#ef4444" fontSize={13} fontWeight={700}>Katsura POS</text>
      {['Dashboard', 'POS Billing', 'Products', 'Customers', 'Purchases', 'Stock Recon', 'Help'].map((label, i) => (
        <g key={label}>
          <rect x={8} y={70 + i * 28} width={144} height={22} fill={i === 6 ? '#ef4444' : 'transparent'} rx={4} />
          <text x={16} y={85 + i * 28} fill="#e5e5e5" fontSize={12}>{label}</text>
        </g>
      ))}
      {/* Main */}
      <rect x={160} y={28} width={560} height={292} fill="#0a0a0a" />
      <text x={184} y={62} fill="#e5e5e5" fontSize={20} fontWeight={700}>Welcome 👋</text>
      <text x={184} y={88} fill="#a3a3a3" fontSize={12}>Two surfaces: this Desktop app per branch + HQ Admin web app.</text>
      <text x={184} y={108} fill="#a3a3a3" fontSize={12}>Default login: admin@katsura.local / change-me-now. Rotate after install.</text>
      <rect x={184} y={130} width={520} height={160} fill="#171717" rx={6} />
      <text x={200} y={156} fill="#fff" fontSize={13} fontWeight={600}>Suggested first steps</text>
      {['1. Settings → upload your store logo + GSTIN', '2. Brands & Categories → set up your catalog scaffold', '3. Products → add SKUs (MRP or LOOSE)', '4. Vendors → onboard suppliers, then GRN', '5. POS → start billing'].map((s, i) => (
        <text key={i} x={200} y={184 + i * 18} fill="#d4d4d4" fontSize={12}>{s}</text>
      ))}
    </Frame>
  );
}

function ProductMrp() {
  return (
    <Frame title="Products → + New product (MRP)">
      <rect x={120} y={48} width={480} height={240} fill="#171717" rx={6} />
      <text x={140} y={76} fill="#fff" fontSize={15} fontWeight={700}>New product</text>
      <FieldRow x={140} y={94} label="SKU" value="HQ-001" />
      <FieldRow x={140} y={120} label="Name" value="Stainless steel kadhai 10in" />
      <FieldRow x={140} y={146} label="Pricing mode" value="MRP ▾" highlight />
      <FieldRow x={140} y={172} label="MRP (₹)" value="₹150.00" highlight />
      <FieldRow x={140} y={198} label="Sale price (₹)" value="₹135.00" />
      <FieldRow x={140} y={224} label="HSN" value="7323 ▾" />
      <rect x={140} y={252} width={120} height={28} fill="#ef4444" rx={4} />
      <text x={200} y={270} fill="#fff" fontSize={12} textAnchor="middle">Save</text>
      <Caption text="MRP shows mrp_paise field; LOOSE hides it." x={460} y={130} />
    </Frame>
  );
}

function ProductLoose() {
  return (
    <Frame title="Products → + New product (LOOSE / DUOM)">
      <rect x={120} y={48} width={480} height={240} fill="#171717" rx={6} />
      <text x={140} y={76} fill="#fff" fontSize={15} fontWeight={700}>New product</text>
      <FieldRow x={140} y={94} label="SKU" value="VEG-TOMATO" />
      <FieldRow x={140} y={120} label="Pricing mode" value="LOOSE ▾" highlight />
      <FieldRow x={140} y={146} label="Stock UOM" value="PCS ▾" />
      <FieldRow x={140} y={172} label="Purchase UOM" value="KG ▾" highlight />
      <FieldRow x={140} y={198} label="DUOM avg weight" value="0.150 kg/pc" highlight />
      <FieldRow x={140} y={224} label="Sale price (₹)" value="₹40.00 / pc" />
      <Caption text="MRP hidden in LOOSE mode." x={460} y={120} />
      <Caption text="DUOM = purchase UOM ≠ stock UOM." x={460} y={172} />
    </Frame>
  );
}

function PurchaseGrn() {
  return (
    <Frame title="Purchases → New GRN">
      <rect x={28} y={48} width={664} height={64} fill="#171717" rx={6} />
      <text x={44} y={68} fill="#a3a3a3" fontSize={10}>GRN header</text>
      <text x={44} y={94} fill="#fff" fontSize={12}>Vendor: Acme Co  •  Mode: MRP  •  State 27  •  Invoice: INV-1</text>
      <rect x={28} y={124} width={664} height={164} fill="#171717" rx={6} />
      <text x={44} y={144} fill="#a3a3a3" fontSize={10}>Lines</text>
      <text x={44} y={166} fill="#e5e5e5" fontSize={11}>HQ-001 · qty 10 · rate ₹135 · batch HQ-001-GRN001</text>
      <text x={44} y={184} fill="#e5e5e5" fontSize={11}>HQ-002 · qty  5 · rate ₹99 ·  batch HQ-002-GRN001</text>
      <text x={44} y={224} fill="#a3a3a3" fontSize={10}>CSV bulk</text>
      <rect x={44} y={232} width={500} height={50} fill="#0a0a0a" rx={4} />
      <text x={56} y={252} fill="#86efac" fontSize={11} fontFamily="monospace">HQ-001,10,135</text>
      <text x={56} y={268} fill="#86efac" fontSize={11} fontFamily="monospace">HQ-002,5,99.50</text>
    </Frame>
  );
}

function BillPos() {
  return (
    <Frame title="POS Billing">
      <rect x={28} y={48} width={420} height={240} fill="#171717" rx={6} />
      <text x={44} y={68} fill="#a3a3a3" fontSize={10}>Scan / search</text>
      <rect x={44} y={76} width={388} height={28} fill="#0a0a0a" stroke="#ef4444" rx={4} />
      <text x={56} y={94} fill="#86efac" fontSize={12} fontFamily="monospace">K3VEFFAAFM01_</text>
      <rect x={44} y={116} width={388} height={156} fill="#0a0a0a" rx={4} />
      <text x={56} y={138} fill="#fff" fontSize={11}>HQ-001  Steel kadhai 10in    1 × ₹135    ₹135.00</text>
      <text x={56} y={158} fill="#fff" fontSize={11}>HQ-002  Tomato (per pc)      3 × ₹40     ₹120.00</text>
      <line x1={44} x2={432} y1={250} y2={250} stroke="#404040" />
      <text x={56} y={266} fill="#a3a3a3" fontSize={11}>Subtotal</text>
      <text x={420} y={266} fill="#fff" fontSize={11} textAnchor="end">₹255.00</text>
      <rect x={460} y={48} width={232} height={240} fill="#171717" rx={6} />
      <text x={476} y={68} fill="#a3a3a3" fontSize={10}>Totals</text>
      <text x={476} y={92} fill="#fff" fontSize={12}>CGST: ₹22.95</text>
      <text x={476} y={108} fill="#fff" fontSize={12}>SGST: ₹22.95</text>
      <text x={476} y={124} fill="#fff" fontSize={12}>Round-off: -₹0.10</text>
      <text x={476} y={152} fill="#fff" fontSize={16} fontWeight={700}>TOTAL ₹300.80</text>
      <rect x={476} y={208} width={200} height={32} fill="#ef4444" rx={4} />
      <text x={576} y={228} fill="#fff" fontSize={13} textAnchor="middle">Pay + Print</text>
      <text x={576} y={260} fill="#86efac" fontSize={10} textAnchor="middle">Prints to Retsol RTP-82UE</text>
    </Frame>
  );
}

function Recon() {
  return (
    <Frame title="Stock Recon → R-20260522-0001">
      <rect x={28} y={48} width={664} height={56} fill="#171717" rx={6} />
      <text x={44} y={70} fill="#fff" fontSize={13} fontWeight={700}>Session R-20260522-0001  •  Status: COUNTING</text>
      <text x={44} y={90} fill="#a3a3a3" fontSize={11}>3 lines · variance -2 PCS · 0 reviewed</text>
      <rect x={28} y={116} width={664} height={172} fill="#171717" rx={6} />
      <text x={44} y={140} fill="#a3a3a3" fontSize={11}>SKU</text>
      <text x={220} y={140} fill="#a3a3a3" fontSize={11}>System</text>
      <text x={320} y={140} fill="#a3a3a3" fontSize={11}>Counted</text>
      <text x={420} y={140} fill="#a3a3a3" fontSize={11}>Variance</text>
      <text x={540} y={140} fill="#a3a3a3" fontSize={11}>Notes</text>
      <ReconRow y={164} sku="HQ-001" sys={50} counted={48} variance={-2} note="damaged x2" />
      <ReconRow y={188} sku="HQ-002" sys={30} counted={30} variance={0} note="" />
      <ReconRow y={212} sku="VEG-TOMATO" sys={12} counted={14} variance={+2} note="found extra" />
      <rect x={540} y={250} width={140} height={28} fill="#ef4444" rx={4} />
      <text x={610} y={269} fill="#fff" fontSize={12} textAnchor="middle">Post adjustments</text>
    </Frame>
  );
}

function BarcodePrint() {
  return (
    <Frame title="Print Barcodes — A4 sheet preview">
      <rect x={28} y={48} width={320} height={240} fill="#171717" rx={6} />
      <text x={44} y={68} fill="#a3a3a3" fontSize={10}>Pick products + qty</text>
      <text x={44} y={94} fill="#fff" fontSize={11}>☑ HQ-001  Steel kadhai   × 6</text>
      <text x={44} y={114} fill="#fff" fontSize={11}>☑ HQ-002  Steel tawa     × 12</text>
      <text x={44} y={134} fill="#fff" fontSize={11}>☐ VEG-TOMATO</text>
      <rect x={44} y={232} width={140} height={32} fill="#ef4444" rx={4} />
      <text x={114} y={252} fill="#fff" fontSize={12} textAnchor="middle">Generate PDF</text>
      <rect x={368} y={48} width={324} height={240} fill="#fff" rx={6} />
      {Array.from({ length: 8 }).map((_, r) => Array.from({ length: 3 }).map((__, c) => (
        <g key={`${r}-${c}`}>
          <rect x={376 + c * 102} y={56 + r * 28} width={94} height={24} fill="#fff" stroke="#d4d4d4" />
          {Array.from({ length: 18 }).map((___, b) => (
            <rect key={b} x={380 + c * 102 + b * 4} y={60 + r * 28} width={(b % 3 === 0) ? 2 : 1} height={12} fill="#000" />
          ))}
          <text x={423 + c * 102} y={78 + r * 28} fontSize={5} textAnchor="middle">K3VEFFAAFM01</text>
        </g>
      )))}
    </Frame>
  );
}

function FieldRow({ x, y, label, value, highlight }) {
  return (
    <g>
      <text x={x} y={y + 12} fill="#a3a3a3" fontSize={11}>{label}</text>
      <rect x={x + 110} y={y} width={260} height={18} fill={highlight ? '#451a1a' : '#0a0a0a'} stroke={highlight ? '#ef4444' : '#262626'} />
      <text x={x + 120} y={y + 13} fill="#fff" fontSize={11}>{value}</text>
    </g>
  );
}

function Caption({ text, x, y }) {
  return (
    <g>
      <rect x={x - 4} y={y - 12} width={text.length * 5 + 16} height={18} fill="#451a1a" stroke="#ef4444" rx={4} />
      <text x={x + 4} y={y + 1} fill="#fecaca" fontSize={10}>{text}</text>
    </g>
  );
}

function ReconRow({ y, sku, sys, counted, variance, note }) {
  return (
    <g>
      <text x={44} y={y} fill="#fff" fontSize={11} fontFamily="monospace">{sku}</text>
      <text x={220} y={y} fill="#a3a3a3" fontSize={11}>{sys}</text>
      <text x={320} y={y} fill="#fff" fontSize={11}>{counted}</text>
      <text x={420} y={y} fill={variance === 0 ? '#a3a3a3' : variance < 0 ? '#fca5a5' : '#86efac'} fontSize={11}>{variance > 0 ? `+${variance}` : variance}</text>
      <text x={540} y={y} fill="#a3a3a3" fontSize={11}>{note}</text>
    </g>
  );
}
